import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { Button, DESTRUCTIVE_GHOST_BUTTON_CLASS } from '#/modules/commonComponents/button';

/** 代號｜名稱｜移除；代號短、名稱有上限，寬螢幕不拉滿整頁 */
const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(7rem,10rem)_minmax(0,24rem)_auto]';

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[var(--z-content-sticky)]',
  'hidden w-full max-w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid @3xl/form-section:w-max',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full max-w-full grid-cols-1 gap-2 px-3 py-2.5',
  '@3xl/form-section:w-max @3xl/form-section:items-center',
  LINE_GRID_COLS,
);

const OPTIONS_LIST_SCROLL_STYLE = {
  '--form-section-scroll-max-height':
    'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

const MtrSpecOptionsEditor = ({ control, register, errors }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });

  function handleAddOption() {
    append({ code: '', name: '' });
  }

  return (
    <div className="form-section @container/form-section w-full">
      <div className="form-section__header">
        <h2 className="form-block__title">規格選項</h2>
        <Button type="button" size="sm" onClick={handleAddOption}>
          新增選項
        </Button>
      </div>

      {fields.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">尚未新增選項，請至少新增一筆。</p>
      ) : null}

      {errors?.options?.message ? (
        <p className="mt-3 text-sm text-red-500">{errors.options.message}</p>
      ) : null}

      {fields.length > 0 ? (
        <div
          className="form-section__scroll"
          style={OPTIONS_LIST_SCROLL_STYLE}
        >
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <p>代號</p>
            <p>名稱</p>
            <p className="justify-self-end text-right">移除</p>
          </div>
          <ul className="m-0 w-full max-w-full list-none divide-y divide-gray-200 p-0 @3xl/form-section:w-max">
            {fields.map((field, idx) => (
              <li key={field.id} className={LINE_ROW_CLASS}>
                <PlainRegisteredInput
                  id={`options.${idx}.code`}
                  register={register}
                  errors={errors}
                  placeholder="選項代號"
                  mobileLabel={`代號 #${idx + 1}`}
                  ariaLabel={`選項代號 ${idx + 1}`}
                />
                <PlainRegisteredInput
                  id={`options.${idx}.name`}
                  register={register}
                  errors={errors}
                  placeholder="選項名稱"
                  mobileLabel="名稱"
                  ariaLabel={`選項名稱 ${idx + 1}`}
                />
                <div className="flex min-w-0 items-center justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                    aria-label={`移除選項 ${idx + 1}`}
                    onClick={() => remove(idx)}
                  >
                    <PiTrashBold className="size-4" aria-hidden />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

MtrSpecOptionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default MtrSpecOptionsEditor;
